import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useMemo } from "react";
import type {
  Contradiction,
  SourceCredibility,
} from "../services/tradingAgentsV2";

interface TrustPanelProps {
  credibility?: SourceCredibility[];
  contradictions?: Contradiction[];
  confidenceBand?: string;
}

interface BandStyle {
  label: string;
  pill: string;
  ring: string;
}

function bandStyle(band: string | undefined, avg: number | null): BandStyle {
  const normalized = (band || "").toLowerCase();
  const resolved =
    normalized ||
    (avg == null
      ? "unknown"
      : avg >= 0.8
        ? "high"
        : avg >= 0.6
          ? "medium"
          : "low");
  switch (resolved) {
    case "high":
      return {
        label: "High confidence",
        pill: "bg-green-100 text-green-800",
        ring: "border-green-200",
      };
    case "medium":
      return {
        label: "Medium confidence",
        pill: "bg-amber-100 text-amber-800",
        ring: "border-amber-200",
      };
    case "low":
      return {
        label: "Low confidence",
        pill: "bg-red-100 text-red-800",
        ring: "border-red-200",
      };
    default:
      return {
        label: "Unscored",
        pill: "bg-gray-100 text-gray-700",
        ring: "border-gray-200",
      };
  }
}

const severityClass = (severity: string): string => {
  switch ((severity || "").toLowerCase()) {
    case "high":
      return "bg-red-100 text-red-700";
    case "medium":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export function TrustPanel({
  credibility,
  contradictions,
  confidenceBand,
}: TrustPanelProps) {
  const stats = useMemo(() => {
    const scores = (credibility ?? [])
      .map((s) => s.credibility)
      .filter((n): n is number => typeof n === "number");
    const avg =
      scores.length > 0
        ? scores.reduce((a, b) => a + b, 0) / scores.length
        : null;
    return {
      scores,
      avg,
      high: scores.filter((s) => s >= 0.8).length,
      medium: scores.filter((s) => s >= 0.6 && s < 0.8).length,
      low: scores.filter((s) => s < 0.6).length,
    };
  }, [credibility]);

  const flagged = contradictions ?? [];

  // Nothing meaningful to show — let the parent fall through to other panels.
  if (stats.scores.length === 0 && flagged.length === 0 && !confidenceBand) {
    return null;
  }

  const style = bandStyle(confidenceBand, stats.avg);
  const pct = stats.avg != null ? Math.round(stats.avg * 100) : null;
  const total = stats.scores.length || 1;
  const seg = (n: number) => `${(n / total) * 100}%`;

  return (
    <section
      className={`rounded-lg border ${style.ring} bg-white p-4 shadow-sm`}
      aria-label="Trust and verifiability"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-primary-600">
          <ShieldCheckIcon className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900">
            Trust &amp; verifiability
          </h3>
          <p className="text-xs text-gray-500">
            How defensible this analysis is, at a glance.
          </p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${style.pill}`}
        >
          {style.label}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <dd className="text-2xl font-bold text-gray-900">
            {pct != null ? `${pct}%` : "—"}
          </dd>
          <dt className="text-xs text-gray-500">Avg source credibility</dt>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <dd className="text-2xl font-bold text-gray-900">
            {stats.scores.length}
          </dd>
          <dt className="text-xs text-gray-500">Sources scored</dt>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <dd
            className={`text-2xl font-bold ${
              flagged.length > 0 ? "text-red-600" : "text-green-600"
            }`}
          >
            {flagged.length}
          </dd>
          <dt className="text-xs text-gray-500">Contradictions flagged</dt>
        </div>
      </dl>

      {stats.scores.length > 0 ? (
        <div className="mt-4">
          <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
            <span className="bg-green-500" style={{ width: seg(stats.high) }} />
            <span className="bg-amber-400" style={{ width: seg(stats.medium) }} />
            <span className="bg-red-400" style={{ width: seg(stats.low) }} />
          </div>
          <div className="mt-2 flex gap-4 text-xs text-gray-500">
            <span>High {stats.high}</span>
            <span>Medium {stats.medium}</span>
            <span>Low {stats.low}</span>
          </div>
        </div>
      ) : null}

      {flagged.length > 0 ? (
        <div className="mt-4">
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
            Disputed across sources
          </h4>
          <ul className="mt-2 space-y-2">
            {flagged.slice(0, 5).map((c, i) => (
              <li
                key={`${c.topic}-${i}`}
                className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-800">{c.topic}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(
                      c.severity
                    )}`}
                  >
                    {c.severity}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-600">
                  <span className="font-medium">A:</span> {c.side_a}
                </p>
                <p className="text-xs text-gray-600">
                  <span className="font-medium">B:</span> {c.side_b}
                </p>
              </li>
            ))}
          </ul>
          {flagged.length > 5 ? (
            <p className="mt-2 text-xs text-gray-500">
              +{flagged.length - 5} more
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-1.5 rounded-lg bg-green-50 p-2.5 text-sm text-green-700">
          <CheckCircleIcon className="h-4 w-4" />
          No contradictions detected across the analyzed sources.
        </div>
      )}
    </section>
  );
}
